import { T } from 'gt-react';
import type { NeedInventoryRow } from '@erp/rpc';

/**
 * On-hand / reserved / available per warehouse holding the SKU, destination
 * first. Shared by the insights panel and the buyer board's Elsewhere
 * tab.
 */
export function InventoryByWarehouseTable({
  rows,
}: {
  rows: NeedInventoryRow[];
}) {
  return (
    <div className="overflow-x-auto rounded border border-gray-200">
      <table className="w-full min-w-max text-xs">
        <thead className="bg-gray-50">
          <tr className="text-left text-gray-600">
            <th className="px-2 py-1 font-medium">
              <T context="inventory table warehouse column">Warehouse</T>
            </th>
            <th className="px-2 py-1 text-right font-medium">
              <T context="inventory table on-hand column">On-hand</T>
            </th>
            <th className="px-2 py-1 text-right font-medium">
              <T context="inventory table reserved column">Reserved</T>
            </th>
            <th className="px-2 py-1 text-right font-medium">
              <T context="inventory table available column">Available</T>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((w) => (
            <tr
              key={w.warehouseId}
              className={
                w.isDestination
                  ? 'border-t border-blue-200 bg-blue-50'
                  : 'border-t border-gray-100'
              }
            >
              <td className="px-2 py-1">
                <div className="flex items-center gap-1 whitespace-nowrap">
                  <span>{w.warehouseName}</span>
                  {w.isDestination && (
                    <span className="rounded bg-blue-200 px-1 text-[10px] uppercase tracking-wide text-blue-900">
                      <T context="destination warehouse marker">dest</T>
                    </span>
                  )}
                  {w.type === 'hub' && (
                    <span className="rounded bg-gray-200 px-1 text-[10px] uppercase tracking-wide text-gray-700">
                      <T context="hub warehouse marker">hub</T>
                    </span>
                  )}
                </div>
                {w.divisionName && (
                  <div className="text-[10px] text-gray-400">
                    {w.divisionName}
                  </div>
                )}
              </td>
              <td className="px-2 py-1 text-right tabular-nums">
                {w.onHandQuantity.toLocaleString()}
              </td>
              <td className="px-2 py-1 text-right tabular-nums text-gray-500">
                {w.reservedQuantity.toLocaleString()}
              </td>
              <td className="px-2 py-1 text-right tabular-nums font-medium">
                {w.availableQuantity.toLocaleString()}
              </td>
            </tr>
          ))}
          {rows.length === 0 && (
            <tr>
              <td colSpan={4} className="px-2 py-3 text-center text-gray-400">
                <T context="no inventory across warehouses empty state">
                  No inventory snapshot data.
                </T>
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
